'use client';

import { Footer } from '@/components/public/footer';
import { NavPreview } from '@/components/public/nav-preview';
import { useScrollReveal } from '@/lib/hooks/use-scroll-reveal';
import { Clock, MapPin, Compass, Trees, Hammer, Mountain, Bike, Palette, Drama, ExternalLink, Flame } from 'lucide-react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { FaqAccordion } from '@/components/public/faq-accordion';
import { FAQ_CATEGORIES } from '@/lib/faq-content';
import { useFaqItems } from '@/lib/faq-hooks';

// ─── Components ──────────────────────────────────────────────────────────────

function ActivityIcon({ type, className = 'w-5 h-5' }: { type: string; className?: string }) {
  switch (type) {
    case 'drama': return <Drama className={className} />;
    case 'palette': return <Palette className={className} />;
    case 'trees': return <Trees className={className} />;
    case 'bike': return <Bike className={className} />;
    case 'bonfire': return <Flame className={className} />;
    default: return <Compass className={className} />;
  }
}

function EstateActivityCard({ activity, index }: { activity: { id: string; name: string; description: string; duration: string; price: string; image: string; icon: string }; index: number }) {
  const isEven = index % 2 === 0;

  return (
    <div
      id={activity.id}
      className={`observe-me ${isEven ? 'slide-left' : 'slide-right'} grid lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 bg-white`}
      style={{ animationDelay: `${index * 0.15}s` }}
    >
      <div className={`relative h-72 lg:h-96 overflow-hidden ${isEven ? '' : 'lg:order-2'}`}>
        <Image
          src={activity.image}
          alt={activity.name}
          fill
          className="object-cover hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent lg:hidden" />
      </div>
      <div className={`flex items-center p-8 lg:p-10 ${isEven ? '' : 'lg:order-1'}`}>
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[#4A6741]/10 flex items-center justify-center">
              <ActivityIcon type={activity.icon} className="w-5 h-5 text-[#4A6741]" />
            </div>
            <div className="flex gap-2">
              <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#4A6741] text-white">
                {activity.duration}
              </span>
              <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#4A6741]/10 text-[#4A6741]">
                {activity.price}
              </span>
            </div>
          </div>
          <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 font-poppins mb-3 leading-tight">
            {activity.name}
          </h3>
          <p className="text-gray-600 leading-relaxed text-lg">{activity.description}</p>
        </div>
      </div>
    </div>
  );
}

function NearbyCard({ activity, index }: { activity: { id: string; name: string; description: string; duration: string; price: string; distance: string; image: string; featured?: boolean; site?: string; hours?: string }; index: number }) {
  const t = useTranslations('design.activities');

  return (
    <div
      id={activity.id}
      className={`observe-me slide-up group relative rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 ${
        activity.featured ? 'md:col-span-2 h-[40rem]' : 'h-[32rem]'
      }`}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <Image
        src={activity.image}
        alt={activity.name}
        fill
        className="object-cover group-hover:scale-105 transition-transform duration-700"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/5" />

      {/* Badges */}
      <div className="absolute top-4 left-4 flex flex-wrap gap-2">
        <span className="bg-white/90 backdrop-blur-sm text-[#4A6741] text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" />
          {activity.duration}
        </span>
        <span className="bg-white/90 backdrop-blur-sm text-[#8B6914] text-xs font-bold px-3 py-1.5 rounded-full">
          {activity.price}
        </span>
        {activity.site && (
          <a
            href={activity.site}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 bg-white/90 backdrop-blur-sm text-[#4A6741] text-xs font-bold px-3 py-1.5 rounded-full hover:bg-white hover:shadow-lg transition-all duration-300"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            {t('visitWebsite')}
          </a>
        )}
      </div>
      <div className="absolute top-4 right-4">
        <span className="bg-[#4A6741]/90 backdrop-blur-sm text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5">
          <MapPin className="w-3.5 h-3.5" />
          {activity.distance}
        </span>
      </div>

      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
        <h3 className={`font-bold text-white font-poppins mb-2 leading-tight ${
          activity.featured ? 'text-2xl lg:text-3xl' : 'text-xl lg:text-2xl'
        }`}>
          {activity.name}
        </h3>
        <p className={`text-white/80 leading-relaxed whitespace-pre-line ${
          activity.featured ? 'text-base lg:text-lg max-w-2xl' : 'text-sm lg:text-base'
        }`}>
          {activity.description}
        </p>
        {activity.hours && (
          <p className="text-white/60 text-sm mt-2 flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" />
            {activity.hours}
          </p>
        )}
      </div>
    </div>
  );
}

function FAQAccordion() {
  const category = FAQ_CATEGORIES.find((c) => c.id === 'activities')!;
  const items = useFaqItems(category);
  return <FaqAccordion items={items} accent={category.accent} />;
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function ActivitiesPage() {
  const t = useTranslations('design.activities');

  const onTheEstate = [
    {
      id: 'exhibition',
      name: t('estate.exhibition.name'),
      description: t('estate.exhibition.description'),
      duration: t('estate.exhibition.duration'),
      price: t('estate.exhibition.price'),
      image: '/design-images/activities/1.jpg',
      icon: 'drama',
    },
    {
      id: 'costume',
      name: t('estate.costume.name'),
      description: t('estate.costume.description'),
      duration: t('estate.costume.duration'),
      price: t('estate.costume.price'),
      image: '/design-images/activities/2.jpg',
      icon: 'palette',
    },
    {
      id: 'goats',
      name: t('estate.goats.name'),
      description: t('estate.goats.description'),
      duration: t('estate.goats.duration'),
      price: t('estate.goats.price'),
      image: '/design-images/activities/3.jpg',
      icon: 'trees',
    },
    {
      id: 'bike',
      name: t('estate.bike.name'),
      description: t('estate.bike.description'),
      duration: t('estate.bike.duration'),
      price: t('estate.bike.price'),
      image: '/design-images/activities/4.jpg',
      icon: 'bike',
    },
    {
      id: 'bonfire',
      name: t('estate.bonfire.name'),
      description: t('estate.bonfire.description'),
      duration: t('estate.bonfire.duration'),
      price: t('estate.bonfire.price'),
      image: '/design-images/uploads/1776846497230-p_11.jpg',
      icon: 'bonfire',
    },
  ];

  const nearby = [
    {
      id: 'gorge',
      name: t('nearby.gorge.name'),
      description: t('nearby.gorge.description'),
      duration: t('nearby.gorge.duration'),
      price: t('nearby.gorge.price'),
      distance: t('nearby.gorge.distance'),
      image: '/design-images/activities/6.jpg',
      featured: true,
    },
    {
      id: 'guide',
      name: t('nearby.guide.name'),
      description: t('nearby.guide.description'),
      duration: t('nearby.guide.duration'),
      price: t('nearby.guide.price'),
      distance: t('nearby.guide.distance'),
      image: '/design-images/activities/7.jpg',
    },
    {
      id: 'furniture',
      name: t('nearby.furniture.name'),
      description: t('nearby.furniture.description'),
      duration: t('nearby.furniture.duration'),
      price: t('nearby.furniture.price'),
      distance: t('nearby.furniture.distance'),
      image: '/design-images/activities/8.jpg',
      site: 'https://www.festettbutor.com/index.php?language=en',
    },
    {
      id: 'adventure-park',
      name: t('nearby.adventurePark.name'),
      description: t('nearby.adventurePark.description'),
      duration: t('nearby.adventurePark.duration'),
      price: t('nearby.adventurePark.price'),
      distance: t('nearby.adventurePark.distance'),
      image: '/design-images/activities/9.jpg',
      site: 'https://www.kortoves.ro/en/kikapcsolodas-en/',
    },
    {
      id: 'textile',
      name: t('nearby.textile.name'),
      description: t('nearby.textile.description'),
      duration: t('nearby.textile.duration'),
      price: t('nearby.textile.price'),
      distance: t('nearby.textile.distance'),
      image: '/design-images/activities/10.jpg',
      site: 'https://www.facebook.com/motollakh/',
    },
    {
      id: 'mastodon',
      name: t('nearby.mastodon.name'),
      description: t('nearby.mastodon.description'),
      duration: t('nearby.mastodon.duration'),
      price: t('nearby.mastodon.price'),
      distance: t('nearby.mastodon.distance'),
      image: '/design-images/activities/11.jpg',
      hours: t('nearby.mastodon.hours'),
      site: 'https://sznm.ro/en/museum/external-units/museum-of-baraolt-basin/',
    },
    {
      id: 'kalnoky',
      name: t('nearby.kalnoky.name'),
      description: t('nearby.kalnoky.description'),
      duration: t('nearby.kalnoky.duration'),
      price: t('nearby.kalnoky.price'),
      distance: t('nearby.kalnoky.distance'),
      image: '/design-images/activities/12.jpg',
      hours: t('nearby.kalnoky.hours'),
      site: 'https://kalnoky.ro/?lan=en',
    },
    {
      id: 'watermill',
      name: t('nearby.watermill.name'),
      description: t('nearby.watermill.description'),
      duration: t('nearby.watermill.duration'),
      price: t('nearby.watermill.price'),
      distance: t('nearby.watermill.distance'),
      image: '/design-images/activities/13.jpg',
      site: 'https://visitcovasna.com/en/places/kisbaconi-v-zimalom',
    },
    {
      id: 'racos',
      name: t('nearby.racos.name'),
      description: t('nearby.racos.description'),
      duration: t('nearby.racos.duration'),
      price: t('nearby.racos.price'),
      distance: t('nearby.racos.distance'),
      image: '/design-images/uploads/1790928653859-bko18b-dsc_9270.jpg',
      site: 'https://maps.app.goo.gl/N8zvRzHoPq3Rbpxs6',
    },
    {
      id: 'offroad',
      name: t('nearby.offroad.name'),
      description: t('nearby.offroad.description'),
      duration: t('nearby.offroad.duration'),
      price: t('nearby.offroad.price'),
      distance: t('nearby.offroad.distance'),
      image: '/design-images/uploads/1772781209881-whatsapp-image-2026-03-05-at-21.12.45.jpg',
      featured: true,
    },
    {
      id: 'cheese',
      name: t('nearby.cheese.name'),
      description: t('nearby.cheese.description'),
      duration: t('nearby.cheese.duration'),
      price: t('nearby.cheese.price'),
      distance: t('nearby.cheese.distance'),
      image: '/design-images/activities/15.jpg',
      site: 'https://www.uzonka.ro/',
    },
    {
      id: 'szekelynationalmuseum',
      name: t('nearby.szekelynationalmuseum.name'),
      description: t('nearby.szekelynationalmuseum.description'),
      duration: t('nearby.szekelynationalmuseum.duration'),
      price: t('nearby.szekelynationalmuseum.price'),
      distance: t('nearby.szekelynationalmuseum.distance'),
      image: '/design-images/uploads/1777380047327-szekely-nemzeti-muzeum.jpg',
      site: 'https://sznm.ro/en/',
      hours: t('nearby.szekelynationalmuseum.hours'),
    },
    {
      id: 'miniTransylvaniapark',
      name: t('nearby.miniTransylvaniapark.name'),
      description: t('nearby.miniTransylvaniapark.description'),
      duration: t('nearby.miniTransylvaniapark.duration'),
      price: t('nearby.miniTransylvaniapark.price'),
      distance: t('nearby.miniTransylvaniapark.distance'),
      image: '/design-images/uploads/1777555846239-mini-erdely-park.jpg',
      site: 'https://parculminitransilvania.ro/minitransylvania-en',
      hours: t('nearby.miniTransylvaniapark.hours'),
    },
    {
      id: 'polixenia',
      name: t('nearby.polixenia.name'),
      description: t('nearby.polixenia.description'),
      duration: t('nearby.polixenia.duration'),
      price: t('nearby.polixenia.price'),
      distance: t('nearby.polixenia.distance'),
      image: '/design-images/uploads/1790780065624-ctqbjh-manor-exterior-land-1400.png',
      site: 'https://polixenia.ro/#visit',
      hours: t('nearby.polixenia.hours'),
    },
    {
      id: 'szekelyEquestrianMuseum',
      name: t('nearby.szekelyEquestrianMuseum.name'),
      description: t('nearby.szekelyEquestrianMuseum.description'),
      duration: t('nearby.szekelyEquestrianMuseum.duration'),
      price: t('nearby.szekelyEquestrianMuseum.price'),
      distance: t('nearby.szekelyEquestrianMuseum.distance'),
      image: '/design-images/uploads/1790850502549-wp4bed-795808958_1614783473351794_3322216404843210707_n.jpg',
      site: 'https://sznm.ro/en/visit/opening-hours/',
      hours: t('nearby.szekelyEquestrianMuseum.hours'),
    },
  ];

  useScrollReveal();

  return (
    <main className="min-h-screen bg-white text-gray-900">
      <style jsx global>{`
        @keyframes breathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }
        @keyframes breatheSlow {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-12px); }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .image-breathe {
          animation: breathe 8s ease-in-out infinite;
        }
        .image-breathe-slow {
          animation: breatheSlow 10s ease-in-out infinite;
        }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      <NavPreview />

      {/* ━━━ HERO ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="relative h-screen w-full overflow-hidden">
        <div className="absolute inset-0">
          <Image
            src="/design-images/activities/6.jpg"
            alt={t('hero.imageAlt')}
            fill
            className="object-cover image-breathe-slow"
            style={{ objectPosition: '50% 60%' }}
            priority
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/20 to-black/70" />

        <div className="relative h-full flex flex-col items-center justify-center px-6 pt-56 lg:pt-16 text-center">
          <div className="observe-me slide-up w-full max-w-3xl">
            <h1 className="font-poppins text-3xl sm:text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight" style={{ textShadow: '2px 2px 30px rgba(0,0,0,0.5)' }}>
              {t('hero.title')}
            </h1>
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className="h-[2px] w-20 bg-[#4A6741]" style={{ animation: 'pulse 3s ease-in-out infinite' }} />
              <p className="text-base md:text-lg uppercase tracking-[0.3em] text-white/90 [text-shadow:rgba(0,0,0,1)_1px_1px_3px] md:[text-shadow:rgb(0,0,0)_1.5px_1.5px_4px]" style={{ backdropFilter: 'blur(1px)', borderRadius: '8px' }}>{t('hero.subtitle')}</p>
              <div className="h-[2px] w-20 bg-[#4A6741]" style={{ animation: 'pulse 3s ease-in-out infinite', animationDelay: '1.5s' }} />
            </div>
            <p className="text-3xl md:text-4xl text-white/95 w-fit max-w-3xl mx-auto font-light [text-shadow:rgba(0,0,0,1)_1.5px_1.5px_4px] md:[text-shadow:rgb(0,0,0)_2px_2px_6px]" style={{ backdropFilter: 'blur(1px)', borderRadius: '8px' }}>
              {t('hero.tagline')}
            </p>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/60">
          <span className="text-xs uppercase tracking-widest">{t('hero.explore')}</span>
          <div className="w-6 h-10 border-2 border-white/40 rounded-full flex items-start justify-center p-1.5">
            <div className="w-1.5 h-3 bg-white/60 rounded-full" style={{ animation: 'float 2s ease-in-out infinite' }} />
          </div>
        </div>

      </section>

      {/* ━━━ INTRO — The Land ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="grid lg:grid-cols-5 min-h-screen design-section-gap rounded-3xl overflow-hidden">
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-[#f5f7f4]">
          <div className="max-w-lg observe-me slide-left">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#4A6741]">{t('intro.headingBold')}</span>
              <br />
              <span className="text-gray-800 text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('intro.headingLight')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#4A6741] mb-6" />
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-6">
              {t('intro.p1')}
            </p>
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-8">
              {t('intro.p2')}
            </p>

            <div className="space-y-4">
              {[
                { icon: Trees, title: t('intro.point1Title'), text: t('intro.point1Text') },
                { icon: Hammer, title: t('intro.point2Title'), text: t('intro.point2Text') },
                { icon: Mountain, title: t('intro.point3Title'), text: t('intro.point3Text') },
              ].map((point, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#4A6741]/10 flex items-center justify-center mt-0.5">
                    <point.icon className="w-5 h-5 text-[#4A6741]" />
                  </div>
                  <div>
                    <span className="font-semibold text-gray-900">{point.title}</span>
                    <p className="text-gray-600 text-sm mt-0.5">{point.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 observe-me slide-right">
          <Image
            src="/design-images/uploads/1772535227162-dji_0328-hdr.jpg"
            alt={t('intro.imageAlt')}
            fill
            className="object-cover image-breathe hover:scale-110 transition-transform duration-700"
            style={{ objectPosition: '50% 50%' }}
          />
        </div>
      </section>

      {/* ━━━ ON THE ESTATE ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="design-section-gap">
        <div className="text-center mb-12 pt-16 observe-me slide-up">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-[#4A6741]/10 flex items-center justify-center">
              <Compass className="w-6 h-6 text-[#4A6741]" />
            </div>
          </div>
          <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-4">
            <span className="text-[#4A6741]">{t('onEstate.heading')}</span>
          </h2>
          <p className="text-gray-800 text-2xl md:text-3xl font-normal">
            {t('onEstate.subheading')}
          </p>
          <div className="w-16 h-1 bg-[#4A6741] mx-auto mt-6 mb-4" />
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('onEstate.description')}
          </p>
        </div>

        <div className="space-y-8">
          {onTheEstate.map((activity, index) => (
            <EstateActivityCard key={activity.id} activity={activity} index={index} />
          ))}
        </div>
      </section>

      {/* ━━━ Full-width image break — Blacksmith ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="grid lg:grid-cols-5 min-h-[90vh] design-section-gap rounded-3xl overflow-hidden">
        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 order-2 lg:order-1 observe-me slide-left">
          <Image
            src="/design-images/activities/5.jpg"
            alt={t('village.imageAlt')}
            fill
            className="object-cover image-breathe hover:scale-110 transition-transform duration-700"
          />
        </div>

        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-gradient-to-br from-[#f9f5f2] to-white order-1 lg:order-2">
          <div className="max-w-lg observe-me slide-right">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#8B6914]">{t('village.headingBold')}</span>
              <br />
              <span className="text-gray-800 text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('village.headingLight')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#8B6914] mb-6" />
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-6">
              {t('village.p1')}
            </p>
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-8">
              {t('village.p2')}
            </p>
            <div className="flex flex-wrap gap-3">
              <span className="bg-[#8B6914] text-white px-5 py-2 rounded-full text-sm font-medium flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {t('village.duration')}
              </span>
              <span className="bg-[#8B6914]/10 text-[#8B6914] px-5 py-2 rounded-full text-sm font-medium">
                {t('village.price')}
              </span>
              <span className="bg-[#8B6914]/10 text-[#8B6914] px-5 py-2 rounded-full text-sm font-medium flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {t('village.distance')}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ━━━ NEARBY — Excursions & Adventures ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="grid lg:grid-cols-5 min-h-screen design-section-gap rounded-3xl overflow-hidden">
        <div className="lg:col-span-2 flex items-center justify-center design-content-pad lg:p-12 xl:p-16 bg-gradient-to-br from-[#f0f4f0] to-white">
          <div className="max-w-lg observe-me slide-left">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 leading-[1.1]">
              <span className="text-[#4A6741]">{t('explore.headingBold')}</span>
              <br />
              <span className="text-gray-800 text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('explore.headingLight')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#4A6741] mb-6" />
            <p className="text-lg xl:text-xl text-gray-600 leading-relaxed mb-5">
              {t('explore.p1')}
            </p>
            <p className="text-lg xl:text-xl text-gray-700 leading-relaxed font-medium">
              {t('explore.p2')}
            </p>
          </div>
        </div>

        <div className="lg:col-span-3 relative h-[50vh] lg:h-auto overflow-hidden bg-gray-100 observe-me slide-right">
          <Image
            src="/design-images/activities/15.jpg"
            alt={t('explore.imageAlt')}
            fill
            className="object-cover image-breathe hover:scale-110 transition-transform duration-700"
          />
        </div>
      </section>

      {/* Nearby activity cards */}
      <section className="design-section-gap">
        <div className="grid md:grid-cols-2 gap-6">
          {nearby.map((activity, index) => (
            <NearbyCard key={activity.id} activity={activity} index={index} />
          ))}
        </div>
      </section>

      {/* ━━━ Full-width image duo — Textile & Adventure ━━━━━━━━━━━━━━━━━━━ */}
      <section className="grid lg:grid-cols-2 h-[50vh] design-section-gap rounded-3xl overflow-hidden observe-me fade-in">
        <div className="relative h-full overflow-hidden">
          <Image
            src="/design-images/activities/10.jpg"
            alt={t('imageDuo.weavingAlt')}
            fill
            className="object-cover hover:scale-105 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          <div className="absolute bottom-5 left-6 right-6">
            <h3 className="text-xl font-bold text-white">{t('imageDuo.weavingTitle')}</h3>
            <p className="text-white/70 text-sm">{t('imageDuo.weavingSubtitle')}</p>
          </div>
        </div>
        <div className="relative h-full overflow-hidden">
          <Image
            src="/design-images/activities/9.jpg"
            alt={t('imageDuo.adventureAlt')}
            fill
            className="object-cover hover:scale-105 transition-transform duration-700"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          <div className="absolute bottom-5 left-6 right-6">
            <h3 className="text-xl font-bold text-white">{t('imageDuo.adventureTitle')}</h3>
            <p className="text-white/70 text-sm">{t('imageDuo.adventureSubtitle')}</p>
          </div>
        </div>
      </section>

      {/* ━━━ DIGITAL MAP ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-b from-white via-[#f5f7f4] to-white overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 text-center observe-me slide-up">
          <div className="w-14 h-14 rounded-full bg-[#4A6741]/10 flex items-center justify-center mx-auto mb-6">
            <MapPin className="w-7 h-7 text-[#4A6741]" />
          </div>
          <h2 className="font-poppins text-4xl md:text-5xl xl:text-6xl font-bold mb-4">
            <span className="text-[#4A6741]">{t('map.heading')}</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-10">
            {t('map.description')}
          </p>
          <a
            href="https://www.google.com/maps/d/u/0/viewer?mid=18voRrqDG4dlz69PRj1CSGqf3fIa4uGs&ll=45.937351333478745%2C25.96145550000001&z=9"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 px-[26px] py-5 bg-[#4A6741] hover:bg-[#3a5534] text-white font-serif text-lg rounded-full transition-all duration-500 shadow-xl hover:shadow-2xl hover:shadow-[#4A6741]/30 transform hover:scale-105"
          >
            {t('map.cta')}
            <ExternalLink className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </a>
          <div className="mt-10 rounded-2xl overflow-hidden shadow-lg">
            <Image
              src="/design-images/activities/16.jpg"
              alt={t('map.imageAlt')}
              width={900}
              height={325}
              className="w-full h-auto"
            />
          </div>
        </div>

        <div className="absolute top-20 right-10 w-2 h-2 bg-[#4A6741] rounded-full opacity-40" style={{ animation: 'pulse 4s ease-in-out infinite' }} />
        <div className="absolute bottom-20 left-16 w-3 h-3 bg-[#8B6914] rounded-full opacity-35" style={{ animation: 'pulse 5s ease-in-out infinite', animationDelay: '1s' }} />
      </section>

      {/* ━━━ FAQ ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <section className="relative py-24 lg:py-32 bg-gradient-to-b from-white to-[#f5f7f4]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16 observe-me slide-up">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6">
              <span className="text-[#4A6741]">{t('faqSection.heading')}</span>
              <br />
              <span className="text-gray-800 text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('faqSection.subheading')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#4A6741] mx-auto" />
          </div>

          <FAQAccordion />
        </div>
      </section>

      <Footer />
    </main>
  );
}
